// Service-worker registration for the auto-update flow per plan/05.
//
// The SW is generated by vite-plugin-pwa with `registerType: 'autoUpdate'`,
// which means: install in background → skipWaiting → clients.claim. We pass
// `immediate: true` so the page reloads silently on controllerchange — no
// "click to refresh" banner. Trade-off: a mid-session reload, accepted
// because kid sessions are short and progress is persisted to IDB.
//
// On top of that we poll the SW URL on an interval (and on visibility
// changes) so a long-lived PWA tab can pick up a new deploy without being
// fully relaunched. The browser's built-in SW update heuristic is rate-
// limited to ~24h, which doesn't match plan/05 §Update flow's "by the end
// of the next launch" promise for tabs that never close.
//
// In dev (`pnpm dev`) the virtual module exports a no-op, so this whole
// file is harmless without the prod build pipeline.

import type { RegisterSWOptions } from 'vite-plugin-pwa/types';

type PwaRegisterModule = {
  registerSW: (options?: RegisterSWOptions) => (reloadPage?: boolean) => Promise<void>;
};

const DEFAULT_POLL_MS = 60 * 60 * 1000;
const MIN_POLL_MS = 5_000;

let activeRegistration: ServiceWorkerRegistration | null = null;
let activeSwUrl: string | null = null;
let pollInFlight = false;

type SwReadyListener = (ready: boolean) => void;
const readyListeners = new Set<SwReadyListener>();

export function isSwReady(): boolean {
  return activeRegistration !== null;
}

// Subscribe to ready-state transitions. Fires the listener synchronously
// with the current state so consumers don't need to read isSwReady()
// separately. Returns an unsubscribe handle for useEffect cleanup. We
// never transition back to `false` — a registered SW stays registered
// for the page's lifetime — so the listener fires at most twice
// (synchronous initial + one-shot on registration).
export function onSwReady(cb: SwReadyListener): () => void {
  readyListeners.add(cb);
  cb(activeRegistration !== null);
  return () => {
    readyListeners.delete(cb);
  };
}

function readPollOverride(): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = new URL(window.location.href).searchParams.get('swPoll');
    if (!raw) return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.max(n, MIN_POLL_MS);
  } catch {
    return null;
  }
}

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && 'onLine' in navigator && !navigator.onLine;
}

async function pollForUpdate(): Promise<boolean> {
  if (!activeRegistration || !activeSwUrl) return false;
  if (pollInFlight) return false;
  if (isOffline()) return false;
  if (activeRegistration.installing) return false;
  pollInFlight = true;
  try {
    const resp = await fetch(activeSwUrl, {
      cache: 'no-store',
      headers: { 'cache-control': 'no-cache' },
    });
    if (!resp.ok) return false;
    await activeRegistration.update();
    return Boolean(activeRegistration.installing);
  } catch (err) {
    console.warn('SW update poll failed', err);
    return false;
  } finally {
    pollInFlight = false;
  }
}

export type CheckForUpdatesResult = 'updated' | 'no-update' | 'offline' | 'unsupported';

// User-triggered update check, surfaced via Settings → Tietoja so a parent
// can force the path on staging without devtools. Same fetch-then-update
// shape as the interval poll but with a richer return type.
export async function checkForUpdatesNow(): Promise<CheckForUpdatesResult> {
  if (!activeRegistration || !activeSwUrl) return 'unsupported';
  if (isOffline()) return 'offline';
  if (activeRegistration.installing) return 'updated';
  const updated = await pollForUpdate();
  return updated ? 'updated' : 'no-update';
}

export async function registerServiceWorker(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  try {
    // The module name is held in a variable so vitest's import-analysis
    // pass doesn't try to resolve it at transform time — vite-plugin-pwa
    // is disabled under VITEST so the virtual module isn't registered
    // there. In dev/prod the PWA plugin still resolves it normally.
    const moduleId = 'virtual:pwa-register';
    const mod = (await import(moduleId)) as PwaRegisterModule;
    const pollMs = readPollOverride() ?? DEFAULT_POLL_MS;
    mod.registerSW({
      immediate: true,
      onRegisteredSW(swUrl, registration) {
        if (!registration) {
          // PWA plugin called us back but the browser produced no
          // ServiceWorkerRegistration — typically not a secure context
          // (http:// outside localhost), the SW URL returned a non-JS
          // Content-Type, or a privacy mode blocked it. Without this
          // log, Settings → "Tarkista päivitykset" hits the
          // 'unsupported' branch with no diagnostic in DevTools.
          console.warn('[sw] onRegisteredSW fired without a registration', { swUrl });
          return;
        }
        console.info('[sw] registered', { swUrl, scope: registration.scope });
        activeRegistration = registration;
        activeSwUrl = swUrl;
        for (const cb of readyListeners) cb(true);

        setInterval(() => {
          if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
          void pollForUpdate();
        }, pollMs);

        if (typeof document !== 'undefined') {
          document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') void pollForUpdate();
          });
        }
      },
      onRegisterError(error) {
        // Async registration failure that the SDK would otherwise
        // swallow. Without this hook we have no signal in DevTools for
        // why activeRegistration stayed null.
        console.warn('[sw] register error', error);
      },
    });
  } catch (err) {
    console.error('[sw] registerServiceWorker failed', err);
  }
}
